import { TransformNode, type AbstractMesh, type Scene } from "@babylonjs/core";
import {
  GATES,
  GATE_LEVERS,
  GATE_OPEN_ANGLE,
  LOOT_CRATES,
  TELESCOPES,
  WORLD_LADDERS,
  landmassAt,
  type GameState,
  type Placement,
} from "@tiny-seas/shared";
import type { Materials } from "./materials.ts";
import type { ModelInstance, SceneryName } from "./models.ts";
import { spawnScenery } from "./scenery.ts";

/** Seconds for a gate door or lever to swing fully. */
const SWING_SECONDS = 1.1;
/** Lever handle throw about its pivot's x axis when the gate is open. */
const LEVER_THROW = -70 * Math.PI / 180;

interface Swing {
  node: TransformNode;
  open: number;
}

/**
 * Animated, state-driven island fixtures: hinged gates and their levers, plunder crates
 * (intact/smashed), mounted telescopes that follow their observer, and invisible frame
 * roots for the tower ladder hand rig. Geometry is never merged into regional batches.
 */
export class IslandFixtures {
  /** Tower ladder frame roots, indexed like WORLD_LADDERS. */
  readonly ladderRoots: TransformNode[];
  private readonly doors: Swing[] = [];
  private readonly levers: (Swing & { gate: number })[] = [];
  private readonly crates: { intact: ModelInstance; broken: ModelInstance; shown: boolean | null }[] = [];
  private readonly telescopes: { head: TransformNode; tube: TransformNode; yaw: number; phase: number }[] = [];
  private time = 0;

  constructor(scene: Scene, mats: Materials, light: (mesh: AbstractMesh) => void) {
    const place = (name: SceneryName, at: Placement): ModelInstance => {
      const theme = landmassAt(at.x, at.z, 2)?.theme ?? "tropical";
      const model = spawnScenery(scene, mats, name, theme);
      model.root.name = `fixture:${name}`;
      model.root.position.set(at.x, at.y, at.z);
      model.root.rotation.y = at.yaw;
      for (const mesh of model.meshes) light(mesh);
      return model;
    };
    this.ladderRoots = WORLD_LADDERS.map((ladder, i) => {
      const root = new TransformNode(`ladder-frame:${i}`, scene);
      root.position.set(ladder.frame.x, ladder.y, ladder.frame.z);
      root.rotation.y = ladder.frame.heading;
      return root;
    });
    for (const gate of GATES) this.doors.push({ node: place("stockadeGate", gate.at).part("door"), open: 0 });
    for (const lever of GATE_LEVERS) this.levers.push({ node: place("gateLever", lever.at).part("handle"), open: 0, gate: lever.gate });
    for (const crate of LOOT_CRATES) {
      const broken = place("lootCrateBroken", crate.at);
      broken.root.setEnabled(false);
      this.crates.push({ intact: place("lootCrate", crate.at), broken, shown: true });
    }
    TELESCOPES.forEach((scope, i) => {
      const model = place("mountedTelescope", scope.at);
      this.telescopes.push({ head: model.part("head"), tube: model.part("tube"), yaw: scope.yaw, phase: i * 2.3 });
    });
  }

  /**
   * `observers[i]` holds the world look of whoever mans telescope i (`active`), else it idles.
   * The local observer's own scope head is hidden: the camera sits at its eyepiece.
   * Gate and crate state come straight from the replicated arrays.
   */
  update(dt: number, state: GameState, observers: readonly { active: boolean; yaw: number; pitch: number }[], localObserver: number, reducedMotion: boolean): void {
    this.time += dt;
    const step = reducedMotion ? 1 : dt / SWING_SECONDS;
    this.doors.forEach((door, i) => {
      door.open = Math.max(0, Math.min(1, door.open + (state.gates[i] ? step : -step)));
      door.node.rotation.y = GATE_OPEN_ANGLE * door.open * door.open * (3 - 2 * door.open);
    });
    for (const lever of this.levers) {
      lever.open = Math.max(0, Math.min(1, lever.open + (state.gates[lever.gate] ? step : -step) * 2));
      lever.node.rotation.x = LEVER_THROW * lever.open * lever.open * (3 - 2 * lever.open);
    }
    this.crates.forEach((crate, i) => {
      const intact = (state.crates[i] ?? 0) <= 0;
      if (crate.shown === intact) return;
      crate.shown = intact;
      crate.intact.root.setEnabled(intact);
      crate.broken.root.setEnabled(!intact);
    });
    this.telescopes.forEach((scope, i) => {
      const observer = observers[i]?.active ? observers[i] : undefined;
      // Unmanned scopes drift slowly, as if the wind nudges the tube.
      const yaw = observer ? observer.yaw - scope.yaw : reducedMotion ? 0 : Math.sin(this.time * .13 + scope.phase) * .5;
      const pitch = observer ? observer.pitch : reducedMotion ? 0 : .05 + Math.sin(this.time * .21 + scope.phase) * .04;
      scope.head.rotation.y = Math.atan2(Math.sin(yaw), Math.cos(yaw));
      scope.tube.rotation.x = pitch;
      const shown = i !== localObserver;
      if (scope.head.isEnabled() !== shown) scope.head.setEnabled(shown);
    });
  }
}
